import React, { createContext, useContext, useState, useEffect } from 'react';
import { teamApi, permissionsApi } from '../services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // Legacy permission state (for backward compatibility)
  const [legacyPermissions, setLegacyPermissions] = useState(null);
  const [role, setRole] = useState(null);
  const [roleLabel, setRoleLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // New RBAC permission state
  const [permissions, setPermissions] = useState({});      // Object CRUD permissions
  const [fieldPermissions, setFieldPermissions] = useState({});  // Field-level exceptions
  const [capabilities, setCapabilities] = useState({});    // Non-CRUD capabilities

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!isAuthenticated) {
        setLegacyPermissions(null);
        setPermissions({});
        setFieldPermissions({});
        setCapabilities({});
        setRole(null);
        setRoleLabel(null);
        setLoading(false);
        return;
      }

      try {
        // Fetch both legacy and new permission formats in parallel
        const [membershipRes, permRes] = await Promise.all([
          teamApi.getMyMembership(),
          permissionsApi.getMyPermissions().catch(() => null)
        ]);
        
        const { member, permissions: legacyPerms } = membershipRes.data;
        
        // Set legacy permissions (backward compatibility)
        setLegacyPermissions(legacyPerms || {});
        setRole(member?.role || null);
        
        // Set new RBAC permissions if available
        if (permRes?.data) {
          setPermissions(permRes.data.permissions || {});
          setFieldPermissions(permRes.data.field_permissions || {});
          setCapabilities(permRes.data.capabilities || {});
          setRoleLabel(permRes.data.role_label || null);
        }
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
        // Only reset permissions if we don't already have them
        if (!legacyPermissions || Object.keys(legacyPermissions).length === 0) {
          setLegacyPermissions({});
          setPermissions({});
          setFieldPermissions({});
          setCapabilities({});
          setRole(null);
          setRoleLabel(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [isAuthenticated, user]);

  // Legacy helper functions (for backward compatibility)
  const canManageCompany = legacyPermissions?.can_manage_company || capabilities?.manage_company || false;
  const canManageTeam = legacyPermissions?.can_manage_team || false;
  const canCrudProducts = legacyPermissions?.can_crud_products || permissions?.products?.update || false;
  const canReorderPriority = legacyPermissions?.can_reorder_priority || capabilities?.reorder_priority || false;
  const canUseAI = legacyPermissions?.can_use_ai || capabilities?.use_ai_copilot || false;
  
  // Role checks
  const isPMOrAbove = role === 'owner' || role === 'product_manager';
  const isOwner = role === 'owner';
  const isCollaborator = role === 'collaborator';
  const isViewer = role === 'viewer';

  // New permission check functions
  const can = (object, action) => {
    const objPerms = permissions[object];
    if (!objPerms) return false;
    return objPerms[action] || false;
  };

  const canField = (object, field) => {
    const key = `${object}.${field}`;
    return fieldPermissions[key] || false;
  };

  const hasCapability = (capability) => {
    return capabilities[capability] || false;
  };

  const value = {
    // New RBAC system
    permissions,
    fieldPermissions,
    capabilities,
    role,
    roleLabel,
    loading,
    
    // New check functions
    can,
    canField,
    hasCapability,
    
    // Legacy permission checks (backward compatibility)
    canManageCompany,
    canManageTeam,
    canCrudProducts,
    canReorderPriority,
    canUseAI,
    
    // Role checks
    isPMOrAbove,
    isOwner,
    isCollaborator,
    isViewer,
    
    // Refresh function
    refreshPermissions: async () => {
      setLoading(true);
      try {
        const [membershipRes, permRes] = await Promise.all([
          teamApi.getMyMembership(),
          permissionsApi.getMyPermissions().catch(() => null)
        ]);
        
        const { member, permissions: legacyPerms } = membershipRes.data;
        
        setLegacyPermissions(legacyPerms || {});
        setRole(member?.role || null);
        
        if (permRes?.data) {
          setPermissions(permRes.data.permissions || {});
          setFieldPermissions(permRes.data.field_permissions || {});
          setCapabilities(permRes.data.capabilities || {});
          setRoleLabel(permRes.data.role_label || null);
        }
      } catch (err) {
        console.error('Failed to refresh permissions:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;
